import { Theme, css } from '@emotion/react';

import type { NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/router';

import { useEffect, useState } from 'react';

import { useLoadingStore } from '@stores/useLoadingStore';
import { Typography } from 'chatie-design-system';

import Loading from '@components/common/Loading';
import Header from '@components/layout/Header';
import Layout from '@components/layout/Layout';
import EmailSignin from '@components/user/EmailSignin';

import chatieLogo from '@public/icons/chatie_origin_logo.png';
import chemingLogo from '@public/images/sign/cheming_login_logo.png';

import { CustomPageProps } from '@model';
import { useMe } from '@utils/hook';
import { getRouterQuery } from '@utils/query';

const SocialSignin = dynamic(() => import('@components/user/SocialSignin'), {
  ssr: false,
});

const headerCSS = (isCheming: boolean) => (theme: Theme) =>
  css`
    .signin__header {
      position: absolute;
      z-index: 9;
      left: 0;
      right: 0;
      text-align: center;
      top: 25.7%;

      &__subtitle {
        color: ${theme.colors.main500};
        ${isCheming ? '' : 'text-align:center;'}
        ${Typography.h5}
      margin-bottom:16px;
      }
    }
  `;
const SignInPage: NextPage<CustomPageProps> = ({ isCheming }) => {
  const [isSigninEmail, setIsSigninEmail] = useState(false);
  const { t } = useTranslation();
  const router = useRouter();
  const next = getRouterQuery(router.query.next) as string;
  const type = getRouterQuery(router.query.type) as string;
  const { data: me } = useMe();

  const { setLoading, isLoading } = useLoadingStore();

  useEffect(() => {
    if (type === 'email') {
      setIsSigninEmail(true);
    }
    if (me) {
      router.replace(next ?? '/');
    }
  }, [me]);

  return (
    <Layout>
      {isSigninEmail && <Header title={t('로그인')} />}
      {isSigninEmail ? (
        <EmailSignin />
      ) : (
        <div css={headerCSS(isCheming)}>
          <div className="signin-bg" />
          <div className="signin__header">
            <p className="signin__header__subtitle">
              캐릭터와 케미가 터지는 순간,
            </p>
            {isCheming ? (
              <Image
                src={chemingLogo}
                alt="logo"
                width={161}
                height={60}
                loader={({ src }) => src}
              />
            ) : (
              <Image
                src={chatieLogo}
                alt="logo"
                width={248}
                loader={({ src }) => src}
              />
            )}
          </div>
          <SocialSignin
            setIsSigninEmail={setIsSigninEmail}
            setLoading={setLoading}
            isLoading={isLoading}
          />
        </div>
      )}
      {isLoading && <Loading center />}
    </Layout>
  );
};

export default SignInPage;
